/**
 * 图层效果参数解析（M3-14，DESIGN.md §7.1 补充：PS 完整 10 种）。鸭子类型，
 * 同 engine/adjustments/resolve.ts 的做法：params 是宽松的 Record，这里按
 * kind 取出所需字段并给出默认值。不 import document/types——engine 不能
 * 反向依赖上层（分层规则）。
 *
 * 效果合成分两个相位（`compositeLayerEffects` 的 phase 参数）：
 * "below"（投影/外发光，画在图层内容下方）与"above"（其余全部，画在
 * 图层内容上方）——真实 PS 的完整堆叠顺序比这更细（比如斜面浮雕实际是
 * "贴在图层表面"而不是简单地叠在上面），这里用两相位模型近似，跟现有
 * stroke/dropShadow 的既有简化保持一致。同一相位内的效果按
 * {@link EFFECT_ORDER} 的固定顺序渲染，不管 `LayerEffect[]` 数组里的
 * 插入顺序是什么——用户在对话框里勾选效果的先后不该影响最终视觉层次。
 */
import {
  renderDropShadow,
  renderInnerGlow,
  renderInnerShadow,
  renderOuterGlow,
  renderSatin,
  renderStroke,
  type BevelEmbossParams,
  type DropShadowParams,
  type GlowParams,
  type InnerShadowParams,
  type SatinParams,
  type StrokeParams,
} from './render';

export type LayerEffectKind =
  | 'dropShadow'
  | 'innerShadow'
  | 'outerGlow'
  | 'innerGlow'
  | 'bevelEmboss'
  | 'satin'
  | 'colorOverlay'
  | 'gradientOverlay'
  | 'patternOverlay'
  | 'stroke';

export interface LayerEffectLike {
  readonly kind: string;
  readonly enabled: boolean;
  readonly params: Readonly<Record<string, number | string>>;
}

/** 固定堆叠顺序（自下而上），见文件头说明。 */
export const EFFECT_ORDER: readonly LayerEffectKind[] = [
  'dropShadow',
  'outerGlow',
  'innerShadow',
  'innerGlow',
  'bevelEmboss',
  'satin',
  'colorOverlay',
  'gradientOverlay',
  'patternOverlay',
  'stroke',
];

/** 属于"below"相位（画在图层内容下方）的效果种类；其余都是"above"。 */
const BELOW_PHASE_KINDS = new Set<LayerEffectKind>(['dropShadow', 'outerGlow']);

export function effectPhase(kind: LayerEffectKind): 'below' | 'above' {
  return BELOW_PHASE_KINDS.has(kind) ? 'below' : 'above';
}

export interface ResolvedShadowLike {
  readonly kind: 'dropShadow' | 'innerShadow';
  readonly color: readonly [number, number, number];
  readonly opacity: number;
  readonly params: DropShadowParams | InnerShadowParams;
}

export interface ResolvedGlow {
  readonly kind: 'outerGlow' | 'innerGlow';
  readonly color: readonly [number, number, number];
  readonly opacity: number;
  readonly params: GlowParams;
}

export interface ResolvedBevelEmboss {
  readonly kind: 'bevelEmboss';
  readonly highlightColor: readonly [number, number, number];
  readonly shadowColor: readonly [number, number, number];
  readonly opacity: number;
  readonly params: BevelEmbossParams;
}

export interface ResolvedSatin {
  readonly kind: 'satin';
  readonly color: readonly [number, number, number];
  readonly opacity: number;
  readonly params: SatinParams;
}

export interface ResolvedStroke {
  readonly kind: 'stroke';
  readonly color: readonly [number, number, number];
  readonly opacity: number;
  readonly params: StrokeParams;
}

export interface ResolvedColorOverlay {
  readonly kind: 'colorOverlay';
  readonly color: readonly [number, number, number];
  readonly opacity: number;
}

export interface ResolvedGradientOverlay {
  readonly kind: 'gradientOverlay';
  /** 简化成两色线性渐变（真实 PS 支持任意多色标预设），角度为度。 */
  readonly from: readonly [number, number, number];
  readonly to: readonly [number, number, number];
  readonly angle: number;
  readonly opacity: number;
}

export interface ResolvedPatternOverlay {
  readonly kind: 'patternOverlay';
  readonly patternId: string;
  readonly scale: number;
  readonly color: readonly [number, number, number];
  readonly opacity: number;
}

export type ResolvedEffect =
  | ResolvedShadowLike
  | ResolvedGlow
  | ResolvedBevelEmboss
  | ResolvedSatin
  | ResolvedStroke
  | ResolvedColorOverlay
  | ResolvedGradientOverlay
  | ResolvedPatternOverlay;

function num(params: LayerEffectLike['params'], key: string, fallback: number): number {
  const value = params[key];
  return typeof value === 'number' ? value : fallback;
}

/** #rrggbb → [r,g,b]。非法输入回退黑色——效果预览不该因为脏字符串崩溃。 */
export function parseColor(value: unknown): readonly [number, number, number] {
  if (typeof value !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(value)) return [0, 0, 0];
  return [
    parseInt(value.slice(1, 3), 16),
    parseInt(value.slice(3, 5), 16),
    parseInt(value.slice(5, 7), 16),
  ];
}

/** 解析一个效果。未启用或种类未识别返回 null。 */
export function resolveEffect(effect: LayerEffectLike): ResolvedEffect | null {
  if (!effect.enabled) return null;
  const { params } = effect;
  const opacity = Math.min(1, Math.max(0, num(params, 'opacity', 1)));
  const color = parseColor(params['color']);

  switch (effect.kind) {
    case 'dropShadow':
      return {
        kind: 'dropShadow',
        color,
        opacity,
        params: {
          distance: num(params, 'distance', 5),
          angle: num(params, 'angle', 135),
          blur: Math.max(0, num(params, 'blur', 5)),
        },
      };
    case 'innerShadow':
      return {
        kind: 'innerShadow',
        color,
        opacity,
        params: {
          distance: num(params, 'distance', 5),
          angle: num(params, 'angle', 135),
          blur: Math.max(0, num(params, 'blur', 5)),
        },
      };
    case 'outerGlow':
      return { kind: 'outerGlow', color, opacity, params: { blur: Math.max(0, num(params, 'blur', 8)) } };
    case 'innerGlow':
      return { kind: 'innerGlow', color, opacity, params: { blur: Math.max(0, num(params, 'blur', 8)) } };
    case 'bevelEmboss':
      return {
        kind: 'bevelEmboss',
        highlightColor: parseColor(params['highlightColor'] ?? '#ffffff'),
        shadowColor: parseColor(params['shadowColor'] ?? '#000000'),
        opacity,
        params: {
          size: Math.max(1, num(params, 'size', 5)),
          angle: num(params, 'angle', 135),
          altitude: num(params, 'altitude', 30),
        },
      };
    case 'satin':
      return {
        kind: 'satin',
        color,
        opacity,
        params: {
          distance: Math.max(1, num(params, 'distance', 10)),
          blur: Math.max(0, num(params, 'blur', 6)),
        },
      };
    case 'stroke': {
      const position = params['position'];
      return {
        kind: 'stroke',
        color,
        opacity,
        params: {
          width: Math.max(1, num(params, 'width', 1)),
          position: position === 'inside' || position === 'center' ? position : 'outside',
        },
      };
    }
    case 'colorOverlay':
      return { kind: 'colorOverlay', color, opacity };
    case 'gradientOverlay':
      return {
        kind: 'gradientOverlay',
        from: parseColor(params['from'] ?? '#000000'),
        to: parseColor(params['to'] ?? '#ffffff'),
        angle: num(params, 'angle', 90),
        opacity,
      };
    case 'patternOverlay':
      return {
        kind: 'patternOverlay',
        patternId: typeof params['patternId'] === 'string' ? params['patternId'] : 'stripes',
        scale: Math.max(0.1, num(params, 'scale', 1)),
        color,
        opacity,
      };
    default:
      return null;
  }
}

/**
 * 对一份 alpha 缓冲渲染效果自身的 alpha（供调用方按颜色/取色混合）。
 * 只覆盖"结果是一份 alpha 遮罩"的几种（阴影/发光/描边）；斜面浮雕给的是
 * 高光/阴影两份遮罩，叠加/渐变/图案叠加不基于形变的 alpha 遮罩，这三类
 * 由 composite.ts 直接按 `ResolvedEffect` 的 kind 分支处理，不走这个函数。
 */
export function renderResolvedEffect(
  effect: ResolvedShadowLike | ResolvedGlow | ResolvedSatin | ResolvedStroke,
  alpha: Uint8Array,
  width: number,
  height: number,
): Uint8Array {
  switch (effect.kind) {
    case 'dropShadow':
      return renderDropShadow(alpha, width, height, effect.params as DropShadowParams);
    case 'innerShadow':
      return renderInnerShadow(alpha, width, height, effect.params as InnerShadowParams);
    case 'outerGlow':
      return renderOuterGlow(alpha, width, height, effect.params);
    case 'innerGlow':
      return renderInnerGlow(alpha, width, height, effect.params);
    case 'satin':
      return renderSatin(alpha, width, height, effect.params);
    case 'stroke':
      return renderStroke(alpha, width, height, effect.params);
  }
}
